import { jsx, jsxs } from "react/jsx-runtime";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from "react";
import axios from "axios";
import {
  Spinner,
  NonIdealState,
  Tooltip,
  Position,
  Icon
} from "@blueprintjs/core";
import Draggable from "react-draggable";
import { Colors } from "@blueprintjs/core";
import {
  computeView,
  getInitialVisibleNodes,
  getVisibleNodesToRemove,
  canCollapse
} from "./GraphLogic";
import testTubeIcon from "./assets/icons/test_tube.png";
const formatMetadata = (nodeName, metadata) => {
  const hasMeta = metadata && Object.keys(metadata).length > 0;
  return /* @__PURE__ */ jsxs("div", { style: { textAlign: "left", fontSize: 11, maxWidth: 250 }, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          fontWeight: "bold",
          borderBottom: "1px solid #ccc",
          marginBottom: 4,
          paddingBottom: 2
        },
        children: nodeName
      }
    ),
    hasMeta && Object.entries(metadata).map(([key, value]) => /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("span", { style: { fontWeight: "bold", textTransform: "capitalize" }, children: [
        key,
        ":"
      ] }),
      " ",
      typeof value === "object" ? JSON.stringify(value) : String(value)
    ] }, key))
  ] });
};
const calculateLayout = (nodes, edges, focusNodeId, placeholders = []) => {
  if (!nodes || nodes.length === 0)
    return { nodes: [], edges: [], placeholders: [], width: 800, height: 600 };
  const nodeMap = {};
  nodes.forEach((n) => nodeMap[n.id] = { ...n, w: 150, h: 60, x: 0, y: 0 });
  const phNodes = {};
  placeholders.forEach((ph) => {
    phNodes[ph.id] = {
      ...ph,
      w: 30,
      h: 30,
      x: 0,
      y: 0,
      isPlaceholder: true
    };
  });
  const ranks = {};
  const parents = {};
  const children = {};
  edges.forEach((e) => {
    if (!children[e.source]) children[e.source] = [];
    children[e.source].push(e);
    if (!parents[e.target]) parents[e.target] = [];
    parents[e.target].push(e);
  });
  placeholders.forEach((ph) => {
    if (ph.type === "left") {
      const phEdge = { source: ph.id, target: ph.parentNodeId, isPlaceholder: true };
      if (!children[ph.id]) children[ph.id] = [];
      children[ph.id].push(phEdge);
      if (!parents[ph.parentNodeId]) parents[ph.parentNodeId] = [];
      parents[ph.parentNodeId].push(phEdge);
    } else {
      const phEdge = { source: ph.parentNodeId, target: ph.id, isPlaceholder: true };
      if (!children[ph.parentNodeId]) children[ph.parentNodeId] = [];
      children[ph.parentNodeId].push(phEdge);
      if (!parents[ph.id]) parents[ph.id] = [];
      parents[ph.id].push(phEdge);
    }
  });
  const queue = [{ id: focusNodeId, rank: 0 }];
  const visited = /* @__PURE__ */ new Set([focusNodeId]);
  ranks[focusNodeId] = 0;
  const allLayoutNodes = { ...nodeMap, ...phNodes };
  while (queue.length > 0) {
    const { id, rank } = queue.shift();
    const nodeParents = parents[id] || [];
    nodeParents.forEach((edge) => {
      const pid = edge.source;
      if (allLayoutNodes[pid] && !visited.has(pid)) {
        visited.add(pid);
        ranks[pid] = rank - 1;
        queue.push({ id: pid, rank: rank - 1 });
      }
    });
    const nodeChildren = children[id] || [];
    nodeChildren.forEach((edge) => {
      const cid = edge.target;
      if (allLayoutNodes[cid] && !visited.has(cid)) {
        visited.add(cid);
        ranks[cid] = rank + 1;
        queue.push({ id: cid, rank: rank + 1 });
      }
    });
  }
  const rankGroups = {};
  let minRank = Infinity;
  let maxRank = -Infinity;
  Object.keys(allLayoutNodes).forEach((id) => {
    const r = ranks[id] !== void 0 ? ranks[id] : 0;
    if (!rankGroups[r]) rankGroups[r] = [];
    rankGroups[r].push(allLayoutNodes[id]);
    minRank = Math.min(minRank, r);
    maxRank = Math.max(maxRank, r);
  });
  const X_SPACING = 250;
  const Y_SPACING = 100;
  const START_X = 100;
  const START_Y = 100;
  Object.keys(rankGroups).forEach((r) => {
    rankGroups[r].forEach((node, idx) => {
      node.y = idx * Y_SPACING;
    });
  });
  const ranksList = Object.keys(rankGroups).map(Number).sort((a, b) => a - b);
  const layoutRank = (r, targetRank, interactionType) => {
    const group = rankGroups[r];
    const nodeBarycenters = group.map((node) => {
      let sumY = 0;
      let count = 0;
      let minOrder = Infinity;
      const adjEdges = interactionType === "children" ? children[node.id] : parents[node.id];
      if (adjEdges) {
        adjEdges.forEach((edge) => {
          const tid = interactionType === "children" ? edge.target : edge.source;
          const neighbor = allLayoutNodes[tid];
          if (neighbor && ranks[tid] === targetRank) {
            sumY += neighbor.y + (neighbor.h || 0) / 2;
            count++;
            if (edge.order !== void 0) {
              minOrder = Math.min(minOrder, edge.order);
            }
          }
        });
      }
      return {
        node,
        barycenter: count > 0 ? sumY / count - (node.h || 0) / 2 : node.y,
        order: minOrder === Infinity ? 999 : minOrder,
        type: node.type
      };
    });
    nodeBarycenters.sort((a, b) => {
      if (Math.abs(a.barycenter - b.barycenter) > 0.1) {
        return a.barycenter - b.barycenter;
      }
      if (a.type === "vector" && b.type !== "vector") return -1;
      if (b.type === "vector" && a.type !== "vector") return 1;
      return a.order - b.order;
    });
    nodeBarycenters.forEach((item) => {
      item.node.y = item.barycenter;
    });
    for (let i = 1; i < nodeBarycenters.length; i++) {
      const prev = nodeBarycenters[i - 1].node;
      const curr = nodeBarycenters[i].node;
      const spacing = (prev.h + curr.h) / 2 + 20;
      if (curr.y < prev.y + spacing) {
        curr.y = prev.y + spacing;
      }
    }
    for (let i = nodeBarycenters.length - 2; i >= 0; i--) {
      const next = nodeBarycenters[i + 1].node;
      const curr = nodeBarycenters[i].node;
      const spacing = (curr.h + next.h) / 2 + 20;
      if (curr.y > next.y - spacing) {
        curr.y = next.y - spacing;
      }
    }
    const avgBarycenter = nodeBarycenters.reduce((sum, item) => sum + item.barycenter, 0) / (nodeBarycenters.length || 1);
    const currentAvgY = nodeBarycenters.reduce((sum, item) => sum + item.node.y, 0) / (nodeBarycenters.length || 1);
    const shiftY = avgBarycenter - currentAvgY;
    nodeBarycenters.forEach((item) => {
      item.node.y += shiftY;
    });
  };
  for (let i = 0; i < 4; i++) {
    for (let j = 1; j < ranksList.length; j++) {
      const r = ranksList[j];
      const prevR = ranksList[j - 1];
      layoutRank(r, prevR, "parents");
    }
    for (let j = ranksList.length - 2; j >= 0; j--) {
      const r = ranksList[j];
      const nextR = ranksList[j + 1];
      layoutRank(r, nextR, "children");
    }
  }
  const distinctY = Object.values(allLayoutNodes).map((n) => n.y);
  const globalMinY = Math.min(...distinctY);
  Object.values(allLayoutNodes).forEach((n) => {
    n.y = n.y - globalMinY + START_Y;
    n.x = START_X + (ranks[n.id] - minRank) * X_SPACING;
    if (n.isPlaceholder) {
      n.x += 60;
    }
  });
  return {
    nodes: Object.values(nodeMap).map((n) => ({ ...n, x: n.x, y: n.y })),
    edges: edges.map((e) => ({
      ...e,
      sourceNode: nodeMap[e.source],
      targetNode: nodeMap[e.target]
    })).filter((e) => e.sourceNode && e.targetNode),
    placeholders: Object.values(phNodes),
    width: (maxRank - minRank + 2) * X_SPACING,
    height: Math.max(...Object.values(rankGroups).map((g) => g.length)) * Y_SPACING + 400,
    focusNodePos: nodeMap[focusNodeId] ? {
      x: nodeMap[focusNodeId].x + nodeMap[focusNodeId].w / 2,
      y: nodeMap[focusNodeId].y + nodeMap[focusNodeId].h / 2
    } : null
  };
};
const GraphViewer = React.forwardRef(({ filePath, onNavigate }, ref) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleNodeIds, setVisibleNodeIds] = useState(/* @__PURE__ */ new Set());
  const [draggablePos, setDraggablePos] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(0.9);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!filePath) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `/api/lineage/${filePath.replace(/^\//, "")}`
        );
        setData(res.data);
        setVisibleNodeIds(getInitialVisibleNodes(res.data.focusNode, res.data));
      } catch (err) {
        console.error(err);
        setError("Failed to load lineage data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filePath]);
  const view = useMemo(() => {
    if (!data) return { nodes: [], edges: [], placeholders: [] };
    return computeView(data, visibleNodeIds);
  }, [data, visibleNodeIds]);
  const layout = useMemo(() => {
    if (!data || view.nodes.length === 0) return null;
    return calculateLayout(
      view.nodes,
      view.edges,
      data.focusNode,
      view.placeholders
    );
  }, [data, view]);
  const handleExpand = (placeholder) => {
    setVisibleNodeIds((prev) => {
      const next = new Set(prev);
      placeholder.hiddenNodeIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const handleCollapse = (nodeId, direction) => {
    const toRemove = getVisibleNodesToRemove(
      nodeId,
      direction,
      visibleNodeIds,
      data,
      data.focusNode
    );
    setVisibleNodeIds((prev) => {
      const next = new Set(prev);
      toRemove.forEach((id) => next.delete(id));
      return next;
    });
  };
  useEffect(() => {
    if (layout && layout.focusNodePos && containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setDraggablePos({
          x: width / 2 - layout.focusNodePos.x,
          y: height / 2 - layout.focusNodePos.y
        });
      }
    }
  }, [layout]);
  const handleDrag = (e, data2) => {
    setDraggablePos({ x: data2.x, y: data2.y });
  };
  const handleWheel = useCallback((e) => {
    if (Math.abs(e.deltaY) < 1) return;
    e.preventDefault();
    const delta = e.deltaY;
    const factor = delta > 0 ? 0.92 : 1.08;
    setScale((prevScale) => {
      const newScale = Math.min(Math.max(prevScale * factor, 0.1), 3);
      if (newScale === prevScale) return prevScale;
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        setDraggablePos((prevPos) => {
          const contentX = (mouseX - prevPos.x) / prevScale;
          const contentY = (mouseY - prevPos.y) / prevScale;
          return {
            x: mouseX - contentX * newScale,
            y: mouseY - contentY * newScale
          };
        });
      }
      return newScale;
    });
  }, []);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || loading) return;
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [handleWheel, loading, data]);
  if (error)
    return /* @__PURE__ */ jsx(NonIdealState, { icon: "error", title: "Error", description: error });
  if (!data && loading) return /* @__PURE__ */ jsx(Spinner, {});
  if (!data || !layout) return /* @__PURE__ */ jsx(NonIdealState, { icon: "graph", title: "No Data" });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: containerRef,
      "data-testid": "graph-container",
      style: {
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: "#f5f5f5",
        position: "relative"
      },
      children: [
        loading && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 100,
              background: "rgba(255,255,255,0.8)",
              padding: 5,
              borderRadius: 4
            },
            children: /* @__PURE__ */ jsx(Spinner, { size: 20 })
          }
        ),
        /* @__PURE__ */ jsx(Draggable, { position: draggablePos, onDrag: handleDrag, scale, children: /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              position: "absolute",
              cursor: "grab"
            },
            children: /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  width: Math.max(2e3, layout.width),
                  height: Math.max(2e3, layout.height),
                  transformOrigin: "0 0",
                  transform: `scale(${scale})`
                },
                children: [
                  /* @__PURE__ */ jsxs("svg", { width: "100%", height: "100%", children: [
                    /* @__PURE__ */ jsxs("defs", { children: [
                      /* @__PURE__ */ jsx(
                        "marker",
                        {
                          id: "arrowhead",
                          markerWidth: "10",
                          markerHeight: "7",
                          refX: "9",
                          refY: "3.5",
                          orient: "auto",
                          children: /* @__PURE__ */ jsx("polygon", { points: "0 0, 10 3.5, 0 7", fill: "#999" })
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        "marker",
                        {
                          id: "arrowhead-blue",
                          markerWidth: "10",
                          markerHeight: "7",
                          refX: "9",
                          refY: "3.5",
                          orient: "auto",
                          children: /* @__PURE__ */ jsx("polygon", { points: "0 0, 10 3.5, 0 7", fill: "#34a1eb" })
                        }
                      )
                    ] }),
                    layout.edges.map((e, i) => {
                      const isOutgoingFromFocus = e.source === data.focusNode;
                      const sx = e.sourceNode.x + e.sourceNode.w;
                      const sy = e.sourceNode.y + e.sourceNode.h / 2;
                      const tx = e.targetNode.x;
                      const ty = e.targetNode.y + e.targetNode.h / 2;
                      const path = `M ${sx} ${sy} C ${(sx + tx) / 2} ${sy}, ${(sx + tx) / 2} ${ty}, ${tx} ${ty}`;
                      return /* @__PURE__ */ jsx(
                        "path",
                        {
                          d: path,
                          stroke: isOutgoingFromFocus ? "#34a1eb" : "#999",
                          strokeWidth: "2",
                          fill: "none",
                          markerEnd: isOutgoingFromFocus ? "url(#arrowhead-blue)" : "url(#arrowhead)"
                        },
                        `edge-${i}`
                      );
                    }),
                    layout.placeholders.map((ph, i) => {
                      const parent = layout.nodes.find((n) => n.id === ph.parentNodeId);
                      if (!parent) return null;
                      let sx, sy, tx, ty;
                      if (ph.type === "left") {
                        sx = ph.x + ph.w;
                        sy = ph.y + ph.h / 2;
                        tx = parent.x;
                        ty = parent.y + parent.h / 2;
                      } else {
                        sx = parent.x + parent.w;
                        sy = parent.y + parent.h / 2;
                        tx = ph.x;
                        ty = ph.y + ph.h / 2;
                      }
                      const path = `M ${sx} ${sy} C ${(sx + tx) / 2} ${sy}, ${(sx + tx) / 2} ${ty}, ${tx} ${ty}`;
                      return /* @__PURE__ */ jsx(
                        "path",
                        {
                          d: path,
                          stroke: "#ccc",
                          strokeWidth: "2",
                          strokeDasharray: "4",
                          fill: "none"
                        },
                        `ph-edge-${i}`
                      );
                    })
                  ] }),
                  layout.placeholders.map((ph) => /* @__PURE__ */ jsx(
                    "div",
                    {
                      onClick: () => handleExpand(ph),
                      style: {
                        position: "absolute",
                        left: ph.x,
                        top: ph.y,
                        width: ph.w,
                        height: ph.h,
                        borderRadius: "50%",
                        background: "white",
                        border: `2px solid ${Colors.GRAY3}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 11,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                      },
                      children: /* @__PURE__ */ jsx(
                        Tooltip,
                        {
                          content: `Expand ${ph.count} more`,
                          position: Position.TOP,
                          children: /* @__PURE__ */ jsx(Icon, { icon: "plus", color: Colors.GRAY1, size: 14 })
                        }
                      )
                    },
                    ph.id
                  )),
                  layout.nodes.map((node) => /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        position: "absolute",
                        left: node.x,
                        top: node.y,
                        width: node.w,
                        height: node.h,
                        zIndex: 10
                        // Ensure nodes are above edges
                      },
                      children: /* @__PURE__ */ jsx(
                        Tooltip,
                        {
                          content: formatMetadata(node.name, node.metadata),
                          position: Position.BOTTOM,
                          targetTagName: "div",
                          targetProps: {
                            style: { width: "100%", height: "100%", display: "block" }
                          },
                          children: /* @__PURE__ */ jsxs(
                            "div",
                            {
                              onClick: () => onNavigate(node.path),
                              onContextMenu: (e) => {
                                if (window.showFileExplorerContextMenu) {
                                  window.showFileExplorerContextMenu(e, node);
                                }
                              },
                              style: {
                                width: 150,
                                height: 60,
                                background: node.isFocus ? Colors.BLUE5 : "white",
                                border: node.isFocus ? `2px solid ${Colors.BLUE3}` : "1px solid #ccc",
                                borderRadius: 4,
                                padding: 8,
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                cursor: "pointer",
                                fontSize: 12,
                                color: node.isFocus ? "white" : "black",
                                boxSizing: "border-box",
                                position: "relative"
                              },
                              children: [
                                (node.inInventory || node.metadata?.inInventory) && /* @__PURE__ */ jsx(
                                  "img",
                                  {
                                    src: testTubeIcon,
                                    style: {
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      width: 14,
                                      height: 14
                                    },
                                    alt: "In Inventory",
                                    title: "In Inventory"
                                  }
                                ),
                                /* @__PURE__ */ jsx(
                                  "div",
                                  {
                                    style: {
                                      fontWeight: "bold",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      lineHeight: "1.2em",
                                      maxHeight: "2.4em",
                                      wordBreak: "break-all",
                                      paddingRight: node.inInventory || node.metadata?.inInventory ? 14 : 0
                                    },
                                    children: node.name
                                  }
                                ),
                                /* @__PURE__ */ jsx(
                                  "div",
                                  {
                                    style: {
                                      opacity: 0.7,
                                      fontSize: 10,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    },
                                    children: node.type
                                  }
                                ),
                                canCollapse(
                                  node.id,
                                  "left",
                                  view.edges,
                                  data.focusNode
                                ) && /* @__PURE__ */ jsx(
                                  "div",
                                  {
                                    onClick: (e) => {
                                      e.stopPropagation();
                                      handleCollapse(node.id, "left");
                                    },
                                    style: {
                                      position: "absolute",
                                      left: -10,
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      width: 20,
                                      height: 20,
                                      borderRadius: "50%",
                                      background: "white",
                                      border: `1.5px solid ${Colors.GRAY3}`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      zIndex: 12,
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                                    },
                                    children: /* @__PURE__ */ jsx(Icon, { icon: "minus", size: 12, color: Colors.GRAY1 })
                                  }
                                ),
                                canCollapse(
                                  node.id,
                                  "right",
                                  view.edges,
                                  data.focusNode
                                ) && /* @__PURE__ */ jsx(
                                  "div",
                                  {
                                    onClick: (e) => {
                                      e.stopPropagation();
                                      handleCollapse(node.id, "right");
                                    },
                                    style: {
                                      position: "absolute",
                                      right: -10,
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      width: 20,
                                      height: 20,
                                      borderRadius: "50%",
                                      background: "white",
                                      border: `1.5px solid ${Colors.GRAY3}`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      zIndex: 12,
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                                    },
                                    children: /* @__PURE__ */ jsx(Icon, { icon: "minus", size: 12, color: Colors.GRAY1 })
                                  }
                                )
                              ]
                            }
                          )
                        }
                      )
                    },
                    node.id
                  ))
                ]
              }
            )
          }
        ) }),
        /* @__PURE__ */ jsx("div", { style: { position: "absolute", top: 10, right: 10, zIndex: 10 }, children: /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              background: "rgba(255,255,255,0.8)",
              padding: 10,
              borderRadius: 4
            },
            children: [
              /* @__PURE__ */ jsx("div", { style: { fontSize: 12, fontWeight: "bold" }, children: "Legend" }),
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", marginTop: 5 }, children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      width: 10,
                      height: 10,
                      background: Colors.BLUE5,
                      marginRight: 5
                    }
                  }
                ),
                " ",
                "Current File"
              ] }),
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", marginTop: 5 }, children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      width: 10,
                      height: 10,
                      background: "white",
                      border: "1px solid #ccc",
                      marginRight: 5
                    }
                  }
                ),
                " ",
                "Related File"
              ] })
            ]
          }
        ) })
      ]
    }
  );
});
export default GraphViewer;
